import React, { useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Check, Calendar, Clock } from 'lucide-react';
import { trackEvent } from '../utils/metaTracking';

const TIME_LABELS = {
  '9am': '9:00 AM', '11am': '11:00 AM',
  '1pm': '1:00 PM', '3pm': '3:00 PM', '5pm': '5:00 PM',
};

const parseSlot = (slotId) => {
  if (!slotId) return null;
  const [datePart, timePart] = slotId.split('_');
  if (!datePart) return null;
  const [year, month, day] = datePart.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return {
    weekday: date.toLocaleDateString('en-US', { weekday: 'long' }),
    month: date.toLocaleDateString('en-US', { month: 'long' }),
    day: String(day),
    year: String(year),
    time: TIME_LABELS[timePart] || timePart || '',
  };
};

const OneTimeSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const slotParam = searchParams.get('slot');
  const appt = parseSlot(slotParam);
  const depositCents = parseInt(searchParams.get('dep') || '', 10);
  const packageId = searchParams.get('pkg') || '';

  // Meta Purchase (browser side). The event_id and value mirror the server-side
  // Conversions API Purchase sent by the Stripe webhook — `purchase_<session>`
  // and the deposit actually charged, USD — so Meta de-duplicates the pair.
  // Guarded per session so a refresh can't double-fire.
  useEffect(() => {
    if (!sessionId || !Number.isFinite(depositCents) || depositCents <= 0) return;
    const guardKey = `meta_purchase_${sessionId}`;
    try {
      if (localStorage.getItem(guardKey)) return;
      localStorage.setItem(guardKey, '1');
    } catch {}
    trackEvent('Purchase', {
      value: depositCents / 100,
      currency: 'USD',
      content_ids: packageId ? [packageId] : undefined,
      content_type: 'product',
      num_items: 1,
    }, `purchase_${sessionId}`);
  }, [sessionId, depositCents, packageId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-[#0d0d0d] flex items-center justify-center px-6 py-16">
      <div className="max-w-xl w-full text-center">

        {/* Check badge */}
        <div className="bg-[#4ade80] rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-8 shadow-lg shadow-[#4ade80]/25">
          <Check className="w-10 h-10 text-black" strokeWidth={3} />
        </div>

        <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">You're Booked!</h1>
        <p className="text-gray-400 text-base mb-10">Your deposit is confirmed. Your spot is locked in.</p>

        {/* ── DATE CARD ── */}
        {appt ? (
          <div className="bg-[#111] border-2 border-[#68caf1] rounded-2xl p-8 mb-8 text-center shadow-xl shadow-[#68caf1]/10">
            <div className="flex items-center justify-center gap-2 text-[#68caf1] text-xs font-bold tracking-widest uppercase mb-5">
              <Calendar className="w-4 h-4" />
              Your Appointment
            </div>
            <p className="text-gray-400 text-sm uppercase tracking-widest mb-1">{appt.weekday}</p>
            <p className="text-white text-6xl md:text-7xl font-black leading-none mb-1">{appt.day}</p>
            <p className="text-white text-2xl font-bold mb-5">{appt.month} {appt.year}</p>
            {appt.time && (
              <div className="inline-flex items-center gap-2 bg-[#68caf1] text-black font-bold text-lg px-6 py-2 rounded-full">
                <Clock className="w-5 h-5" />
                {appt.time}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
            <p className="text-gray-300 text-sm">Check your email — your appointment date and time are in your confirmation.</p>
          </div>
        )}

        {/* What happens next */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8 text-left">
          <h3 className="text-[#68caf1] font-bold text-sm uppercase tracking-widest mb-4">What Happens Next</h3>
          <ol className="text-gray-300 text-sm space-y-3 list-decimal list-inside leading-relaxed">
            <li>You'll get a confirmation email with your full booking details</li>
            <li>We'll text you the day before to confirm your time</li>
            <li>Our team arrives at your address — just make sure your vehicle is accessible</li>
          </ol>
        </div>

        {/* Maintenance upsell — separate program, not covered by the ad promo codes */}
        <div className="bg-gradient-to-br from-[#68caf1]/15 to-transparent border border-[#68caf1]/40 rounded-2xl p-6 mb-8 text-left">
          <p className="text-[#68caf1] font-bold text-xs uppercase tracking-widest mb-2">Keep it this clean</p>
          <h3 className="text-white text-xl font-bold mb-2">Interior Maintenance — from $75/month</h3>
          <p className="text-gray-300 text-sm leading-relaxed mb-4">
            Start with the Alchemist Reset (from $200), then Akeem comes back once a month on the same day to keep it spotless — vacuum, wipe-down, windows, spot steam. Sedan $75 · SUV $80 · Truck $85, flat rate, no condition surcharges.
          </p>
          <Link
            to="/maintenance"
            onClick={() => trackEvent('ViewContent', { content_name: 'maintenance_upsell_success', content_category: 'maintenance' })}
            className="inline-block bg-[#68caf1] text-black px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-[#5bb8db] transition-all"
          >
            See the Maintenance Program
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/detail"
            className="inline-block bg-[#68caf1] text-black px-8 py-3 rounded-lg font-bold hover:bg-[#5bb8db] transition-all"
          >
            Book Another Detail
          </Link>
          <Link
            to="/"
            className="inline-block bg-white/10 text-white px-8 py-3 rounded-lg font-bold hover:bg-white/20 transition-all"
          >
            Back to Home
          </Link>
        </div>

        {sessionId && (
          <p className="text-xs text-gray-700 mt-8">Ref: {sessionId.slice(-12)}</p>
        )}
      </div>
    </div>
  );
};

export default OneTimeSuccessPage;
